import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import type { components } from "@/lib/api/types";
import { useAuthStore } from "@/stores/auth-store";

type UserResponse = components["schemas"]["UserResponse"];

/**
 * Hook to access the full auth state
 */
export function useAuth() {
	const { user, isAuthenticated, isLoading, isInitialized, accessToken, refreshToken, clearAuth } =
		useAuthStore();

	return {
		user,
		isAuthenticated,
		isLoading,
		isInitialized,
		accessToken,
		refreshToken,
		logout: clearAuth,
	};
}

/**
 * Hook to access just the current user
 */
export function useUser(): UserResponse | null {
	return useAuthStore((state: { user: UserResponse | null }) => state.user);
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticated(): boolean {
	return useAuthStore((state: { isAuthenticated: boolean }) => state.isAuthenticated);
}

/**
 * Hook to require authentication - redirects to login if not authenticated
 * Use this in protected routes/components
 */
export function useRequireAuth(redirectTo = "/login"): {
	isLoading: boolean;
	isAuthenticated: boolean;
} {
	const navigate = useNavigate();
	const { isAuthenticated, isInitialized } = useAuthStore();

	useEffect(() => {
		// Only redirect once we've finished initializing
		// (to avoid flickering if user is already logged in)
		if (isInitialized && !isAuthenticated) {
			navigate({ to: redirectTo });
		}
	}, [isInitialized, isAuthenticated, navigate, redirectTo]);

	return {
		isLoading: !isInitialized,
		isAuthenticated,
	};
}

/**
 * Hook to redirect authenticated users away from auth pages
 * Use this on login/signup pages
 */
export function useRedirectIfAuthenticated(redirectTo = "/"): void {
	const navigate = useNavigate();
	const { isAuthenticated, isInitialized } = useAuthStore();

	useEffect(() => {
		// Only redirect once we've finished initializing
		if (isInitialized && isAuthenticated) {
			navigate({ to: redirectTo });
		}
	}, [isInitialized, isAuthenticated, navigate, redirectTo]);
}
